/**
 * Design-system reference (Sprint-1 deliverable visual proof).
 *
 * Renders every locked primitive in its correct semantic context so the
 * owner can verify the foundation matches AVBP_FLAGSHIP_B_v2 exactly:
 * the four button meanings, the one-primary rule, chips vs bubble, stage
 * rail, bounded panels, tags, focus rings, pure-white, AA.
 *
 * This is the foundation showcase only — NOT the Review Queue screen
 * (that is Sprint 3). It uses Review-Queue-shaped sample content purely
 * to demonstrate the components in their intended setting.
 */
import {
  AiBubble,
  AiChip,
  Box,
  Btn3,
  Card,
  EmptyNote,
  Lnk,
  Pill,
  StageRail,
  Tag,
} from '../components/ui';
import { useToast } from '../components/Toast';

export default function DesignSystem() {
  const { toast } = useToast();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 920 }}>
      <Box
        title="Semantic button system"
        note="Ink neutral · Green progression · Brass ceremonial (ink label) · Terracotta = links only"
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <Btn3 intent="ink">Send (neutral operational)</Btn3>
          <Btn3 intent="grn">Commit (progression)</Btn3>
          <Btn3 intent="au">⟡ Release report (ceremonial)</Btn3>
          <Btn3 intent="ghost">Reject (secondary)</Btn3>
          <Btn3 intent="ink" size="sm">
            Small ink
          </Btn3>
          <Btn3 intent="grn" size="sm">
            Small green
          </Btn3>
        </div>
        <p style={{ marginTop: 14, fontSize: 12.5, color: 'var(--ink-soft)' }}>
          Press any button — Direction B physics: gradient face, 2px key
          shadow, 3px press-in. A link is terracotta and never a fill:{' '}
          <Lnk onClick={() => toast('Source link tapped')}>Source ↗</Lnk>
        </p>
      </Box>

      <Box title="One coloured primary per zone" note="Focal-lane shape (sample data)">
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Tag kind="find">create_finding</Tag>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--warn)' }}>
              needs remedy
            </span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 5 }}>
            toilet_in_SE — MD cabin block
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginBottom: 14 }}>
            Sample card — exactly one coloured primary (Commit), the rest neutral.
          </div>
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <Btn3 intent="grn" onClick={() => toast('Committed to record', { ok: true })}>
              Commit
            </Btn3>
            <Btn3 intent="ghost">Reject</Btn3>
            <Btn3 intent="ghost">Edit</Btn3>
            <Btn3 intent="ghost">Source ↗</Btn3>
          </div>
        </Card>
      </Box>

      <Box title="AI chip (ambient) vs AI bubble (needs your call)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <AiChip>
            <b>4 proposals</b> from message + 3 photos · auto-tagged zones E, SE{' '}
            <Lnk>Review ↗</Lnk>
          </AiChip>
          <AiChip good>
            <b>Rohan Agarwal</b> added · Client Circle · site coordinator
          </AiChip>
          <AiBubble
            actions={
              <>
                <button className="mini pri">Add as Client Circle</button>
                <button className="mini">Change role…</button>
                <button className="mini">Not now</button>
              </>
            }
          >
            Add <b>Rohan Agarwal</b> as a project participant on VP656?
          </AiBubble>
        </div>
      </Box>

      <Box title="Stage rail · pills · tags">
        <StageRail
          stages={[
            { label: 'Engaged', state: 'done' },
            { label: 'Intake', state: 'now' },
            { label: 'Report 1', state: 'todo' },
            { label: 'R1→R2 gate', state: 'todo' },
            { label: 'Follow-up', state: 'todo' },
          ]}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <Pill on>All</Pill>
          <Pill>Findings</Pill>
          <Pill>Intake</Pill>
          <Pill>Photos</Pill>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <Tag>update_intake</Tag>
          <Tag kind="find">create_finding</Tag>
          <Tag kind="tag">photo_tag ×3</Tag>
          <Tag kind="val">value moment</Tag>
        </div>
      </Box>

      <Box title="Empty state" note="reusable">
        <EmptyNote title="Nothing here yet">
          Bounded, headlined, calm — the system never dead-ends the user.
        </EmptyNote>
      </Box>
    </div>
  );
}
